import { _isNil, _isNotBlank, _notNil } from '@/littledash';
import { type AnimalApiId } from '@/model/Animal.model';
import { type AnimalDefinitionApiId } from '@/model/PlannedAnimal.model';
import {
  type SampleDefinitionMetadataV1,
  type TempAnimalApiId,
  type TempAnimalDefinitionApiId,
} from '@/model/SamplePlan.model';
import { timepointComparator } from '@/components/UI/SpreadSheet/editors/TimepointComponent';
import { DateUtils } from '@/utils/Date.utils';
import { type useApiHook } from '@/support/Hooks/api/useApiHook';
import {
  type GroupDimension,
  type MetaField,
  type SampleTypeMap,
  type AnimalMap,
  type StaticGroupDimension,
  STATIC_DIMENSION_LABELS,
  isMetaGroupDimension,
} from './SampleNames.model';

type _AggInvoke = Parameters<
  ReturnType<
    typeof useApiHook<'GET /api/v1/studies/{studyApiId}/planned/samples/reserved-samples/aggregations'>
  >['invoke']
>[0];
/** Query params type for the reserved-samples aggregations endpoint. */
export type AggQueryParams = NonNullable<_AggInvoke>['query'];

export const STATIC_DIMENSION_FILTER_KEY: Record<StaticGroupDimension, string> = {
  sampleDefinition: 'sample_definition_api_id',
  studyGroup: 'study_group_api_id',
  animal: 'animal_api_id',
  sampleType: 'sample_type_api_id',
  plannedAnimal: 'animal_definition_api_id',
  timepoint: 'timepoint',
};

/** Key returned by the aggregations endpoint for samples with no study group. */
export const NO_STUDY_GROUP_KEY = 'unassigned';

/**
 * @param groupByDimension - the dimension to group results by
 * @param filterDimensions - parent dimension/key pairs to filter by (one per tree level)
 */
export const buildAggQuery = (
  groupByDimension: GroupDimension,
  filterDimensions: Array<{ dimension: GroupDimension; key: string }>
): AggQueryParams => {
  const query: Record<string, string | boolean | null | undefined> = {};
  if (isMetaGroupDimension(groupByDimension)) {
    query['group_by'] = 'sampleMeta';
    query['group_meta_glossary_api_id'] = groupByDimension.apiId;
  } else {
    query['group_by'] = groupByDimension;
  }
  filterDimensions.forEach(({ dimension, key }) => {
    if (isMetaGroupDimension(dimension)) {
      query['filter_meta_glossary_api_id'] = dimension.apiId;
      query['meta_value'] = key;
    } else if (dimension === 'studyGroup' && key === NO_STUDY_GROUP_KEY) {
      query['no_study_group'] = true;
    } else {
      query[STATIC_DIMENSION_FILTER_KEY[dimension]] = key;
    }
  });
  return query as AggQueryParams;
};

export const dimensionKey = (dimension: GroupDimension): string =>
  isMetaGroupDimension(dimension) ? `sampleMeta:${dimension.apiId}` : dimension;

export const getDimensionLabel = (dimension: GroupDimension, metaFields: Array<MetaField>): string => {
  if (isMetaGroupDimension(dimension)) {
    return metaFields.find((f) => f.apiId === dimension.apiId)?.title ?? dimension.apiId;
  }
  return STATIC_DIMENSION_LABELS[dimension];
};

export const capitalise = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

export const renderTimepoint = (tp: string): string => {
  const displayValue = DateUtils.renderDuration(tp, { format: 'short', defaultResponse: tp });
  return displayValue === 'none' ? 'N/A' : displayValue;
};

export const renderTimepointList = (timepoints: Array<string>): string =>
  timepoints
    .reduce<Array<string>>((acc, tp) => {
      if (_isNotBlank(tp)) {
        acc.push(tp);
      }
      return acc;
    }, [])
    .sort(timepointComparator)
    .map(renderTimepoint)
    .join(', ');

export const renderSampleTypes = (typeIds: Array<string>, map: SampleTypeMap): string =>
  typeIds
    .map((id) => map.get(id) ?? id)
    .sort((a, b) => a.localeCompare(b))
    .join(', ');

/**
 * Resolves the display string for a tree-node key, applying dimension-specific formatting
 * (timepoint rendering, sample type lookup, animal name resolution).
 */
export const resolveDisplayValue = (
  dimension: GroupDimension,
  key: string,
  name: string | null | undefined,
  sampleTypeMap: SampleTypeMap,
  animalMap: AnimalMap
): string => {
  if (dimension === 'timepoint') {
    return renderTimepoint(key);
  }
  if (dimension === 'sampleType') {
    return sampleTypeMap.get(key) ?? key;
  }
  if (dimension === 'animal') {
    const entry = animalMap.get(key as AnimalApiId | TempAnimalApiId);
    return (_notNil(entry) && entry.type === 'registered' ? entry.name : null) ?? name ?? key;
  }
  if (dimension === 'plannedAnimal') {
    const entry = animalMap.get(key as AnimalDefinitionApiId | TempAnimalDefinitionApiId);
    return (_notNil(entry) && entry.type === 'planned' ? entry.planned_id : null) ?? name ?? key;
  }
  return name ?? key;
};

export const renderCount = (count: number): string => `${count} total`;

export const getMetaValue = (metadata: Array<SampleDefinitionMetadataV1>, glossaryApiId: string): string => {
  const entry = metadata.find((m) => m.type === glossaryApiId);
  if (_isNil(entry)) {
    return '';
  }
  const val = entry.value;
  return typeof val === 'string' ? val : '';
};

export const toggleSetIds = <T>(prev: Set<T>, ids: Array<T>): Set<T> => {
  const allPresent = ids.every((id) => prev.has(id));
  if (allPresent) {
    const next = new Set(prev);
    ids.forEach((id) => next.delete(id));
    return next;
  }
  return new Set([...prev, ...ids]);
};
